// shell/components/bulk-actions/bulk-action-bar.ts

import { IDisposable } from '../types';
import { EventListenerTracker } from '../renderer-utils';

/**
 * Option for dropdown actions.
 */
export interface BulkActionOption {
    key: string;
    label: string;
    icon?: string;
    /** Return false to hide the option */
    visible?: (items: unknown[]) => boolean;
}

/**
 * Definition for a bulk action.
 */
export interface BulkAction<T = unknown> {
    key: string;
    label: string;
    icon?: string;

    /** Execute action on selected items */
    execute: (items: T[], optionKey?: string) => Promise<void>;

    /** Whether action can be executed (greys out if false) */
    canExecute?: (items: T[]) => boolean | Promise<boolean>;

    /** Visual variant */
    variant?: 'default' | 'primary' | 'danger';

    /** Dropdown options (if action has sub-choices) */
    options?: BulkActionOption[];
}

/**
 * Configuration for BulkActionBar.
 */
export interface BulkActionBarConfig<T = unknown> {
    /** Container element to render into */
    container: HTMLElement;

    /** Available actions */
    actions: BulkAction<T>[];

    /** Called when user clicks "Clear selection" */
    onClearSelection?: () => void;

    /** Accessibility label */
    ariaLabel?: string;

    /** Show selection count (default: true) */
    showSelectionCount?: boolean;

    /** Position variant (default: 'bottom') */
    position?: 'top' | 'bottom' | 'floating';

    /** Called when an action is executed successfully */
    onActionExecuted?: (actionKey: string, items: T[]) => void;

    /** Called when an action fails */
    onActionError?: (actionKey: string, error: Error, items: T[]) => void;
}

interface ActionButtonState<T = unknown> {
    action: BulkAction<T>;
    button: HTMLButtonElement;
    disabled: boolean;
    dropdown?: {
        menu: HTMLElement;
        isOpen: boolean;
    };
}

/**
 * BulkActionBar displays and executes actions for selected items.
 *
 * Features:
 * - Shows selection count with clear button
 * - Renders action buttons with optional dropdown menus
 * - Supports async canExecute checks
 * - Keyboard navigation (Arrow keys, Enter/Space)
 * - Loading state during action execution
 * - ARIA accessibility
 *
 * Usage:
 * ```typescript
 * const actionBar = new BulkActionBar({
 *     container: document.getElementById('action-bar-container'),
 *     actions: [
 *         {
 *             key: 'delete',
 *             label: 'Delete',
 *             icon: '🗑️',
 *             variant: 'danger',
 *             execute: async (items) => { await deleteItems(items); }
 *         },
 *         {
 *             key: 'export',
 *             label: 'Export',
 *             options: [
 *                 { key: 'csv', label: 'Export as CSV' },
 *                 { key: 'json', label: 'Export as JSON' }
 *             ],
 *             execute: async (items, format) => { await exportItems(items, format); }
 *         }
 *     ],
 *     onClearSelection: () => selectionManager.clear()
 * });
 *
 * // Update when selection changes
 * selectionManager.on('change', (items) => {
 *     actionBar.setSelectedItems(items);
 * });
 * ```
 */
export class BulkActionBar<T = unknown> implements IDisposable {
    private readonly config: BulkActionBarConfig<T>;
    private readonly eventTracker = new EventListenerTracker();
    private readonly element: HTMLElement;
    private readonly infoElement: HTMLElement;
    private readonly countElement: HTMLElement;
    private readonly buttonsContainer: HTMLElement;
    private readonly actionStates = new Map<string, ActionButtonState<T>>();

    private selectedItems: T[] = [];
    private loading = false;
    private visible = false;
    private focusedButtonIndex = -1;

    constructor(config: BulkActionBarConfig<T>) {
        this.config = config;
        this.element = this.createRootElement();
        this.infoElement = this.element.querySelector('.cs-bulk-actions__info')!;
        this.countElement = this.element.querySelector('.cs-bulk-actions__count')!;
        this.buttonsContainer = this.element.querySelector('.cs-bulk-actions__buttons')!;

        this.renderActionButtons();
        this.setupKeyboardNavigation();

        config.container.appendChild(this.element);
    }

    private createRootElement(): HTMLElement {
        const el = document.createElement('div');
        el.className = 'cs-bulk-actions';
        el.setAttribute('role', 'toolbar');
        el.setAttribute('aria-label', this.config.ariaLabel ?? 'Actions for selected items');
        el.setAttribute('aria-hidden', 'true');

        // Position variant
        const position = this.config.position ?? 'bottom';
        if (position !== 'bottom') {
            el.classList.add(`cs-bulk-actions--${position}`);
        }

        el.innerHTML = `
            <div class="cs-bulk-actions__info">
                <span class="cs-bulk-actions__count">0 selected</span>
                <button type="button" class="cs-bulk-actions__clear" aria-label="Clear selection">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                    </svg>
                </button>
            </div>
            <div class="cs-bulk-actions__buttons" role="group"></div>
        `;

        // Clear selection handler
        const clearBtn = el.querySelector('.cs-bulk-actions__clear') as HTMLButtonElement;
        this.eventTracker.add(clearBtn, 'click', () => {
            this.config.onClearSelection?.();
        });

        return el;
    }

    private renderActionButtons(): void {
        this.buttonsContainer.innerHTML = '';
        this.actionStates.clear();

        for (const action of this.config.actions) {
            const state = this.createActionButton(action);
            this.actionStates.set(action.key, state);
            this.buttonsContainer.appendChild(state.dropdown?.menu?.parentElement ?? state.button);
        }
    }

    private createActionButton(action: BulkAction<T>): ActionButtonState<T> {
        const hasDropdown = action.options && action.options.length > 0;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cs-bulk-actions__btn';
        button.setAttribute('data-action', action.key);

        if (action.variant && action.variant !== 'default') {
            button.classList.add(`cs-bulk-actions__btn--${action.variant}`);
        }

        // Button content
        let html = '';
        if (action.icon) {
            html += `<span class="cs-bulk-actions__btn-icon" aria-hidden="true">${action.icon}</span>`;
        }
        html += `<span class="cs-bulk-actions__btn-label">${action.label}</span>`;

        if (hasDropdown) {
            html += `
                <svg class="cs-bulk-actions__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
                </svg>
            `;
            button.setAttribute('aria-haspopup', 'true');
            button.setAttribute('aria-expanded', 'false');
        }

        button.innerHTML = html;

        const state: ActionButtonState<T> = {
            action,
            button,
            disabled: false
        };

        // Create dropdown if needed
        if (hasDropdown) {
            const wrapper = document.createElement('div');
            wrapper.className = 'cs-bulk-actions__dropdown';

            const menu = this.createDropdownMenu(action, state);
            wrapper.appendChild(button);
            wrapper.appendChild(menu);

            state.dropdown = {
                menu,
                isOpen: false
            };

            // Dropdown toggle handler
            this.eventTracker.add(button, 'click', (e) => {
                e.stopPropagation();
                this.toggleDropdown(state);
            });
        } else {
            // Direct action execution
            this.eventTracker.add(button, 'click', () => {
                this.executeAction(action);
            });
        }

        // Keyboard handler
        this.eventTracker.add(button, 'keydown', (e) => {
            this.handleButtonKeyDown(e as KeyboardEvent, state);
        });

        return state;
    }

    private createDropdownMenu(action: BulkAction<T>, state: ActionButtonState<T>): HTMLElement {
        const menu = document.createElement('ul');
        menu.className = 'cs-bulk-actions__menu';
        menu.setAttribute('role', 'menu');
        menu.setAttribute('aria-label', `${action.label} options`);

        for (const option of action.options!) {
            const item = document.createElement('li');
            item.className = 'cs-bulk-actions__menu-item';
            item.setAttribute('role', 'menuitem');
            item.setAttribute('tabindex', '-1');
            item.setAttribute('data-option', option.key);

            let html = '';
            if (option.icon) {
                html += `<span class="cs-bulk-actions__menu-icon" aria-hidden="true">${option.icon}</span>`;
            }
            html += `<span>${option.label}</span>`;
            item.innerHTML = html;

            this.eventTracker.add(item, 'click', () => {
                this.closeDropdown(state);
                this.executeAction(action, option.key);
            });

            this.eventTracker.add(item, 'keydown', (e) => {
                this.handleMenuItemKeyDown(e as KeyboardEvent, state, item);
            });

            menu.appendChild(item);
        }

        return menu;
    }

    private toggleDropdown(state: ActionButtonState<T>): void {
        if (!state.dropdown) return;

        if (state.dropdown.isOpen) {
            this.closeDropdown(state);
        } else {
            this.openDropdown(state);
        }
    }

    private openDropdown(state: ActionButtonState<T>): void {
        if (!state.dropdown || state.dropdown.isOpen) return;

        // Close other dropdowns
        for (const s of this.actionStates.values()) {
            if (s !== state && s.dropdown?.isOpen) {
                this.closeDropdown(s);
            }
        }

        state.dropdown.isOpen = true;
        state.dropdown.menu.classList.add('cs-bulk-actions__menu--open');
        state.button.setAttribute('aria-expanded', 'true');

        // Update option visibility
        this.updateDropdownVisibility(state);

        // Focus first visible option
        const firstOption = state.dropdown.menu.querySelector('[role="menuitem"]:not([hidden])') as HTMLElement;
        firstOption?.focus();

        // Close on outside click
        const closeHandler = (e: Event) => {
            if (!state.dropdown?.menu.contains(e.target as Node) && e.target !== state.button) {
                this.closeDropdown(state);
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    private closeDropdown(state: ActionButtonState<T>): void {
        if (!state.dropdown || !state.dropdown.isOpen) return;

        state.dropdown.isOpen = false;
        state.dropdown.menu.classList.remove('cs-bulk-actions__menu--open');
        state.button.setAttribute('aria-expanded', 'false');
        state.button.focus();
    }

    private updateDropdownVisibility(state: ActionButtonState<T>): void {
        if (!state.dropdown) return;

        const action = state.action;
        if (!action.options) return;

        const menuItems = state.dropdown.menu.querySelectorAll('[role="menuitem"]');
        menuItems.forEach((item, index) => {
            const option = action.options![index];
            const visible = option.visible?.(this.selectedItems) ?? true;
            (item as HTMLElement).hidden = !visible;
            item.setAttribute('aria-disabled', String(!visible));
        });
    }

    private handleButtonKeyDown(event: KeyboardEvent, state: ActionButtonState<T>): void {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (state.dropdown) {
                    this.openDropdown(state);
                }
                break;
            case 'Escape':
                if (state.dropdown?.isOpen) {
                    event.preventDefault();
                    this.closeDropdown(state);
                }
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.focusPreviousButton();
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.focusNextButton();
                break;
        }
    }

    private handleMenuItemKeyDown(event: KeyboardEvent, state: ActionButtonState<T>, currentItem: HTMLElement): void {
        const menu = state.dropdown?.menu;
        if (!menu) return;

        const items = Array.from(menu.querySelectorAll('[role="menuitem"]:not([hidden])')) as HTMLElement[];
        const currentIndex = items.indexOf(currentItem);

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (currentIndex < items.length - 1) {
                    items[currentIndex + 1].focus();
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (currentIndex > 0) {
                    items[currentIndex - 1].focus();
                } else {
                    this.closeDropdown(state);
                }
                break;
            case 'Escape':
                event.preventDefault();
                this.closeDropdown(state);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                currentItem.click();
                break;
            case 'Tab':
                this.closeDropdown(state);
                break;
        }
    }

    private setupKeyboardNavigation(): void {
        this.eventTracker.add(this.element, 'keydown', (e) => {
            const event = e as KeyboardEvent;
            if (event.key === 'Escape') {
                // Close any open dropdowns
                for (const state of this.actionStates.values()) {
                    if (state.dropdown?.isOpen) {
                        this.closeDropdown(state);
                        event.preventDefault();
                        return;
                    }
                }
            }
        });
    }

    private focusPreviousButton(): void {
        const buttons = this.getActionButtons();
        if (buttons.length === 0) return;

        const current = document.activeElement as HTMLElement;
        const currentIndex = buttons.indexOf(current);

        if (currentIndex > 0) {
            buttons[currentIndex - 1].focus();
        } else if (currentIndex === 0) {
            // Wrap to last
            buttons[buttons.length - 1].focus();
        }
    }

    private focusNextButton(): void {
        const buttons = this.getActionButtons();
        if (buttons.length === 0) return;

        const current = document.activeElement as HTMLElement;
        const currentIndex = buttons.indexOf(current);

        if (currentIndex >= 0 && currentIndex < buttons.length - 1) {
            buttons[currentIndex + 1].focus();
        } else if (currentIndex === buttons.length - 1) {
            // Wrap to first
            buttons[0].focus();
        }
    }

    private getActionButtons(): HTMLElement[] {
        return Array.from(this.buttonsContainer.querySelectorAll('.cs-bulk-actions__btn'));
    }

    private async executeAction(action: BulkAction<T>, optionKey?: string): Promise<void> {
        if (this.loading) return;
        if (this.selectedItems.length === 0) return;

        // Check canExecute
        const state = this.actionStates.get(action.key);
        if (state?.disabled) return;

        this.setLoading(true);

        try {
            await action.execute(this.selectedItems, optionKey);
            this.config.onActionExecuted?.(action.key, this.selectedItems);
        } catch (error) {
            console.error('Bulk action failed:', action.key, error);
            this.config.onActionError?.(action.key, error as Error, this.selectedItems);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Set the selected items.
     * Automatically shows/hides the bar based on selection count.
     */
    setSelectedItems(items: T[]): void {
        this.selectedItems = items;
        this.updateSelectionCount();

        if (items.length > 0) {
            this.show();
            // Update action states asynchronously
            this.updateActionStates();
        } else {
            this.hide();
        }
    }

    /**
     * Get the currently selected items.
     */
    getSelectedItems(): T[] {
        return this.selectedItems;
    }

    private updateSelectionCount(): void {
        const count = this.selectedItems.length;
        const showCount = this.config.showSelectionCount ?? true;

        if (showCount) {
            this.countElement.textContent = `${count} selected`;
        } else {
            this.countElement.textContent = '';
        }
    }

    /**
     * Update enabled/disabled state for all action buttons.
     * Call this when external conditions might have changed.
     */
    async updateActionStates(): Promise<void> {
        const promises: Promise<void>[] = [];

        for (const state of this.actionStates.values()) {
            const action = state.action;

            if (action.canExecute) {
                const promise = (async () => {
                    const canExecute = await action.canExecute!(this.selectedItems);
                    this.setActionDisabled(state, !canExecute);
                })();
                promises.push(promise);
            } else {
                // No canExecute = always enabled
                this.setActionDisabled(state, false);
            }
        }

        await Promise.all(promises);
    }

    private setActionDisabled(state: ActionButtonState<T>, disabled: boolean): void {
        state.disabled = disabled;
        state.button.disabled = disabled;
        state.button.setAttribute('aria-disabled', String(disabled));
    }

    /**
     * Show the action bar.
     */
    show(): void {
        if (this.visible) return;
        this.visible = true;
        this.element.classList.add('cs-bulk-actions--visible');
        this.element.setAttribute('aria-hidden', 'false');
    }

    /**
     * Hide the action bar.
     */
    hide(): void {
        if (!this.visible) return;
        this.visible = false;
        this.element.classList.remove('cs-bulk-actions--visible');
        this.element.setAttribute('aria-hidden', 'true');

        // Close any open dropdowns
        for (const state of this.actionStates.values()) {
            if (state.dropdown?.isOpen) {
                this.closeDropdown(state);
            }
        }
    }

    /**
     * Check if the action bar is visible.
     */
    isVisible(): boolean {
        return this.visible;
    }

    /**
     * Set loading state.
     * When loading, action buttons are disabled and the bar shows a loading indicator.
     */
    setLoading(loading: boolean): void {
        this.loading = loading;
        this.element.classList.toggle('cs-bulk-actions--loading', loading);

        // Disable all buttons during loading
        for (const state of this.actionStates.values()) {
            state.button.disabled = loading || state.disabled;
        }
    }

    /**
     * Update the available actions.
     */
    setActions(actions: BulkAction<T>[]): void {
        (this.config as { actions: BulkAction<T>[] }).actions = actions;
        this.renderActionButtons();
        this.updateActionStates();
    }

    /**
     * Focus the first action button.
     */
    focus(): void {
        const buttons = this.getActionButtons();
        if (buttons.length > 0) {
            buttons[0].focus();
        }
    }

    /**
     * Get the root element.
     */
    getElement(): HTMLElement {
        return this.element;
    }

    dispose(): void {
        this.eventTracker.dispose();
        this.element.remove();
        this.actionStates.clear();
    }
}
